import IconComment from "assets/img/icon-comment-empty.svg?react"

import { FC, useLayoutEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import AssetMaintenance, { AssetMaintenanceStatus } from "models/AssetMaintenance"
import { Grid, GridItem } from "components/Grid"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import useMaintenanceTypes from "hooks/useMaintenanceTypes"
import Text from "components/Text/Text"
import AssetMaintenanceStatusTag from "components/AssetMaintenanceStatusTag/AssetMaintenanceStatusTag"
import Button from "components/Button/Button"
import { Flex, FlexItem } from "components/Flex"
import alertModals from "stores/alertModals"
import AssetMaintenanceControls from "./AssetMaintenanceControls"
import Tooltip from "components/Tooltip/Tooltip"

interface IAssetMaintenancesItemProps {
    maintenance: AssetMaintenance
    assetOperationHours?: number | null
    onChanged: () => Promise<void>
}

const AssetMaintenancesItem: FC<IAssetMaintenancesItemProps> = ({
    maintenance,
    assetOperationHours,
    onChanged,
}) => {
    const { t } = useTranslation()
    const [isCommentOverflow, setIsCommentOverflow] = useState(false)
    const commentElRef = useRef<HTMLDivElement>(null)

    const { maintenanceTypesMap } = useMaintenanceTypes()
    const typeName = maintenanceTypesMap.get(maintenance.typeId)?.name

    const operationHoursDiff = maintenance.getOperationHoursDiff(assetOperationHours ?? null)

    // detect that text is clamped
    useLayoutEffect(() => {
        if (!commentElRef.current) return
        const elem = commentElRef.current
        setIsCommentOverflow(elem.scrollHeight > elem.clientHeight)
    }, [maintenance.completedComment, commentElRef])

    const showFullComment = isCommentOverflow
        ? () => {
            if (!maintenance.completedComment) return
            alertModals.show({
                theme: "secondary",
                title: t("common.comment"),
                maxWidth: "586px",
                children: <Text preWrap>{maintenance.completedComment}</Text>,
            })
        }
        : null

    return (
        <div className="border-grey-100-between py-1-5">
            <Grid columns="257px minmax(0, 230px) minmax(0, auto)" gap="1.2rem">
                <GridItem>
                    {typeName && <FlexibleContent text={typeName} />}
                    <Text type="pSmallest" className="mt-half color-grey-500">
                        {!!maintenance.thresholdDate &&
                            <div>{t('maintenance.thresholdDate')}: <strong>{maintenance.thresholdDate.format(t('config.dateLongFormat'))}</strong></div>
                        }
                        {!!maintenance.thresholdOperationHours &&
                            <div>{t('maintenance.thresholdOperatingHours')}: <strong>{t('common.xHours', { count: maintenance.thresholdOperationHours })}</strong></div>
                        }
                    </Text>
                </GridItem>
                <GridItem>
                    <AssetMaintenanceStatusTag status={maintenance.status} />
                    <Text type="pSmallest" className="mt-half color-grey-500">
                        {maintenance.status === AssetMaintenanceStatus.completed
                            ? (maintenance.completedDate
                                ? `${t('maintenance.completedOn')}: ${maintenance.completedDate.format(t('config.dateLongFormat'))}`
                                : ""
                            )
                            : <>
                                {maintenance.daysDiff !== null &&
                                    <div>
                                        <strong>{t('common.xDays', { count: Math.abs(maintenance.daysDiff) })}</strong> {maintenance.daysDiff < 0 ? t('maintenance.overdue') : t('maintenance.remaining')}
                                    </div>
                                }
                                {operationHoursDiff !== null &&
                                    <div>
                                        <strong>{t('maintenance.xOperatingHours', { count: Math.abs(operationHoursDiff) })}</strong> {operationHoursDiff < 0 ? t('maintenance.overdue') : t('maintenance.remaining')}
                                    </div>
                                }
                            </>
                        }
                    </Text>
                </GridItem>
                <GridItem>
                    <AssetMaintenanceControls
                        maintenance={maintenance}
                        onChanged={onChanged}
                    />
                </GridItem>
            </Grid>
            {maintenance.completedComment &&
                <Flex className="mt-1-5 color-grey-500" gap="0.8rem">
                    <FlexItem shrink={0}>
                        <IconComment className="d-block" />
                    </FlexItem>
                    <Text type="pSmallest" className="pt-half">
                        <div ref={commentElRef} className="clamp-2 white-space-pre-wrap">{maintenance.completedComment}</div>
                        {!!showFullComment &&
                            <div className="d-inline-block mt-half">
                                <Tooltip content={maintenance.completedComment}>
                                    <Button
                                        theme="link"
                                        label={t('maintenance.showText')}
                                    />
                                </Tooltip>
                            </div>
                        }
                    </Text>
                </Flex>
            }
        </div>
    )
}

export default AssetMaintenancesItem
